import _, {find} from 'lodash';
import React, {FC, PropsWithChildren, useCallback, useEffect, useMemo, useState} from 'react';

import {globalWindow} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {
  getIsUserGlobalAdmin,
  getUserRoleByNamespace,
} from '../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {isTeamAdmin} from '../../../../helpers/models/accessHelpers';
import {RSettingsWorkflowsUrl} from '../../../../models/url/models/workflowsUrlModel';
import {parseSettingsUrl} from '../../../../models/url/settingsUrlModel';
import {AppState} from '../../../../store/appState';
import {getHasSetupGuide, getIsInTrialOrManagedTrial} from '../../../../store/data/company/companySelectors';
import {getLastSettingsPathVisited} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {
  getSetupGuideIsCompleted,
  getWasOnboardingCompleted,
  getWorkflowsOnboardingCompleted,
} from '../../../../store/data/preferences/preferencesSelectors';
import {getUserNamespace} from '../../../../store/data/teammates/teammatesSelectors';
import {getSortedAccessibleTeams} from '../../../../store/data/teams/teamsSelectors';
import {makeGetAppPath} from '../../../../store/panels/app/appPanelSelectors';
import {useLegacyFrameContext} from '../../../legacy/legacyFrameContext';
import {AdminSetupGuideScreensEnum} from '../../../onboarding/adminSetupGuide/adminSetupGuideTypes';
import {
  ContinueSetupTipsEnum,
  OnboardingModalContext,
  OnboardingModalContextProps,
  useOnboardingModalState,
} from './onboardingContext';

/*
 * Constants.
 */

const onboardingModalDelay = 800;

export function useMaybeOpenOnboardingModalUponInitialLoad(panelId: AppPanelId) {
  const wasOnboardingCarouselCompleted = useAppSelector(getWasOnboardingCompleted);
  const isInTrial = useAppSelector(getIsInTrialOrManagedTrial);
  const {setIsOnboardingChecklistModalOpen} = useOnboardingModalState();
  const {findNewLegacyRoute} = useLegacyFrameContext();

  const hasCompletedAllTasks = useAppSelector(getSetupGuideIsCompleted);
  // Don't show checklist if the workflows video is open and in the workflows path
  const workflowsOnboardingCompleted = useAppSelector(getWorkflowsOnboardingCompleted);
  const appPath = useAppSelector((state: AppState) => makeGetAppPath()(state, {panelId}));
  const userNamespace = useAppSelector(getUserNamespace);

  useInitialEffect(() => {
    const legacyRoute = findNewLegacyRoute(appPath);
    const settingsUrl = legacyRoute ? parseSettingsUrl(legacyRoute.match, userNamespace) : undefined;
    const isWorkFlowsVideoOpen = !workflowsOnboardingCompleted && RSettingsWorkflowsUrl.is(settingsUrl);

    if (wasOnboardingCarouselCompleted && isInTrial && !hasCompletedAllTasks && !isWorkFlowsVideoOpen) {
      setIsOnboardingChecklistModalOpen(true);
    }
  });
}

export const OnboardingModalProvider: FC<PropsWithChildren> = (props) => {
  const {children} = props;
  const [isOnboardingChecklistModalOpen, setIsOnboardingChecklistModalOpen] = useState(false);
  const [adminSetupGuideScreen, setAdminSetupGuideScreen] = useState<AdminSetupGuideScreensEnum | undefined>(
    globalWindow.location.hash === '#show-admin-onboarding' ? AdminSetupGuideScreensEnum.DEFAULT : undefined,
  );

  const isSetupGuideEnabled = useAppSelector(getHasSetupGuide);
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const sortedAccessibleTeams = useAppSelector(getSortedAccessibleTeams);
  const roleByNamespace = useAppSelector(getUserRoleByNamespace);
  const lastSettingsPathVisited = useAppSelector(getLastSettingsPathVisited);

  const hasCompletedAllTasks = useAppSelector(getSetupGuideIsCompleted);

  // When we only have one accessible team, we show all teams settings under "Company".
  const shouldShowTeamsInDropdown = sortedAccessibleTeams.length > 1;

  const getInitialSelectedTeam = () => {
    const firstTeamUserIsTeamAdminOf = _(sortedAccessibleTeams)
      .filter((team) => Boolean(isTeamAdmin(team.namespace, roleByNamespace)))
      .first();

    const hasP0SetupGuide = isSetupGuideEnabled && isGlobalAdmin && !hasCompletedAllTasks;

    // Global admins don't have an initially selected team.
    if (isGlobalAdmin) {
      // In the multi-team situation, if we're in setup, we auto-populate the first non-sandbox team.
      if (hasP0SetupGuide && shouldShowTeamsInDropdown) {
        return firstTeamUserIsTeamAdminOf;
      }
      return undefined;
    }
    // Only select a team if accessible teams length > 1.
    // When accessible teams length is 1, we return undefined because we show all the team settings items under "Company".
    // If user isn't any kind of admin, we will also return undefined.
    return shouldShowTeamsInDropdown ? firstTeamUserIsTeamAdminOf : undefined;
  };

  const [settingsSelectedTeam, setSettingsSelectedTeam] = useState<TeamModel | undefined>(
    getInitialSelectedTeam(),
  );

  // We update the selected team state according to the browser URL.
  // This is necessary to handle the case of clicking the back/forward buttons in the browser.
  useEffect(() => {
    if (!lastSettingsPathVisited) {
      return;
    }
    const splitPath = lastSettingsPathVisited.split('/');
    // Check if this is a team setting URL.
    if (lastSettingsPathVisited.includes('tim:')) {
      const pathTeamNamespace = find(splitPath, (pathItem) => pathItem.startsWith('tim:'));
      const team = find(sortedAccessibleTeams, (t) => t.namespace === pathTeamNamespace);
      if (team && shouldShowTeamsInDropdown) {
        setSettingsSelectedTeam(team);
      }
    } else if (!lastSettingsPathVisited.includes('settings/home')) {
      // There isn't 'tim:' in the path, so the only case we're still in the team namespace is admin home.
      // If we're not admin home, the selected team should be undefined.
      setSettingsSelectedTeam(undefined);
    }
  }, [lastSettingsPathVisited, sortedAccessibleTeams, shouldShowTeamsInDropdown]);

  const wasOnboardingCompleted = useAppSelector(getWasOnboardingCompleted);
  const isInTrial = useAppSelector(getIsInTrialOrManagedTrial);

  const [isAdminWizardOpen, setIsAdminWizardOpen] = useState(false);

  const shouldOpenOnboardingChecklist = useCallback(
    () =>
      // Only open the checklist if in a trial and the onboarding slides were completed
      isInTrial && wasOnboardingCompleted,
    [isInTrial, wasOnboardingCompleted],
  );

  const maybeOpenOnboardingChecklist = useCallback((): boolean => {
    const shouldOpenChecklist = shouldOpenOnboardingChecklist();
    if (shouldOpenChecklist) {
      globalWindow.setInstrumentedTimeout(() => {
        setIsOnboardingChecklistModalOpen(true);
        trackInterfaceEvent(InterfaceEventsEnum.P0_GETTING_STARTED_OPEN, {
          type: 'auto',
        });
      }, onboardingModalDelay);
    }

    return shouldOpenChecklist;
  }, [shouldOpenOnboardingChecklist]);

  const [requestedContinueSetupTip, setRequestedContinueSetupTip] = useState<
    ContinueSetupTipsEnum | undefined
  >(undefined);

  const value: OnboardingModalContextProps = useMemo(
    () => ({
      isAdminSetupGuideOpen: adminSetupGuideScreen !== undefined,
      defaultAdminSetupGuideScreen: adminSetupGuideScreen ?? AdminSetupGuideScreensEnum.DEFAULT,
      openAdminSetupGuide: () => setAdminSetupGuideScreen(AdminSetupGuideScreensEnum.DEFAULT),
      openAdminSetupGuideDemoScreen: () => setAdminSetupGuideScreen(AdminSetupGuideScreensEnum.DEMOS),
      openAdminSetupGuideSetupScreen: () => setAdminSetupGuideScreen(AdminSetupGuideScreensEnum.SETUP),
      closeAdminSetupGuide: () => setAdminSetupGuideScreen(undefined),
      isOnboardingChecklistModalOpen,
      setIsOnboardingChecklistModalOpen,
      shouldOpenOnboardingChecklist,
      maybeOpenOnboardingChecklist,
      settingsSelectedTeam,
      setSettingsSelectedTeam,
      isAdminWizardOpen,
      setIsAdminWizardOpen,
      requestedContinueSetupTip,
      requestContinueSetupTip: setRequestedContinueSetupTip,
      clearRequestedContinueSetupTip: () => setRequestedContinueSetupTip(undefined),
    }),
    [
      isAdminWizardOpen,
      isOnboardingChecklistModalOpen,
      shouldOpenOnboardingChecklist,
      maybeOpenOnboardingChecklist,
      settingsSelectedTeam,
      adminSetupGuideScreen,
      requestedContinueSetupTip,
      setRequestedContinueSetupTip,
    ],
  );

  return <OnboardingModalContext.Provider value={value}>{children}</OnboardingModalContext.Provider>;
};
